import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router';
import { CircleArrowUp, LogOut, Moon, Settings as SettingsIcon, Sun } from 'lucide-react';
import { signOutToLogin } from '@core/auth/signOut';
import { devAuthEmail, devAuthUser } from '@config/env';
import { routes } from '@config/routes';
import { useTheme } from '@shared/theme';
import { useMe } from '@features/user-profile/hooks/useMe';
import type { MeSubscription } from '@features/user-profile/hooks/useMe';
import { usePlans, type Plan } from '@features/billing/api';
import { featurePlans } from '@config/env';

/** `s.charAt(0).toUpperCase() + s.slice(1)` — the fallback display name when the catalog hasn't
 * loaded (or has no matching row) yet: `'core'` → `'Core'`, never the raw lowercase wire value. */
function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** The catalog's own display name for the caller's tier (I2, 2026-08-13) — replaces the old
 * hardcoded `TIER_DISPLAY` map so the menu shows whatever name the backoffice/catalog gives a tier
 * ("Free" today, whatever it's renamed to tomorrow) instead of a client-side guess. Falls back to a
 * capitalized tier key while `usePlans()` is loading/erroring or the catalog has no matching row. */
function planDisplayName(tier: MeSubscription['tier'], plans: Plan[] | undefined): string {
  return plans?.find(p => p.tier === tier)?.name ?? capitalize(tier);
}

/**
 * Sidebar profile control: a circular avatar (user's initial) at the foot of the icon rail that
 * opens a dropdown to its right with the signed-in email, a light/dark segmented toggle, the
 * billing "Upgrade plan" row, Settings, and Log out. Replaces the email + Sign-out that used to
 * live in the Topbar.
 *
 * Auth is branched like the Topbar: `useAuth()` is only called on the Cognito path because in
 * dev-auth mode the OIDC `AuthProvider` isn't mounted (see app/providers.tsx). `useMe()` has no such
 * restriction (it only needs the QueryClientProvider, which wraps both branches), so it is read ONCE
 * here — unconditionally, keeping the hook-call order identical every render — and the resulting
 * subscription is passed down as a prop to the shared `ProfileMenuView` rather than having that view
 * (or the dev-auth branch) call `useMe()` itself.
 */
export function ProfileMenu() {
  const me = useMe();
  const subscription = me.data?.subscription ?? null;
  if (devAuthUser) {
    return (
      <ProfileMenuView
        email={devAuthEmail || 'dev'}
        onSignOut={() => { /* no Cognito session in dev-auth */ }}
        subscription={subscription}
      />
    );
  }
  return <CognitoProfileMenu subscription={subscription} />;
}

function CognitoProfileMenu({ subscription }: { subscription: MeSubscription | null }) {
  const auth = useAuth();
  const email =
    (auth.user?.profile.email as string | undefined) ??
    (auth.user?.profile['cognito:username'] as string | undefined) ??
    (auth.user?.profile['preferred_username'] as string | undefined) ??
    'Signed in';

  return <ProfileMenuView email={email} onSignOut={signOutToLogin} subscription={subscription} />;
}

function ProfileMenuView({
  email, onSignOut, subscription,
}: {
  email: string;
  onSignOut: () => void;
  /** From `useMe().data?.subscription` — NEVER re-derived from the OIDC token, which carries no
   *  billing claims. `null` means the billing feature is dark, so the row is omitted entirely. */
  subscription: MeSubscription | null;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // Only feeds the row below (omitted entirely when `subscription` is null) — called unconditionally
  // regardless, like `useMe()` above it, so hook-call order never depends on runtime state.
  const plans = usePlans();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initial = (email.trim()[0] ?? '?').toUpperCase();

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Account menu"
        title={email}
        className="flex h-9 w-9 items-center justify-center rounded-full text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        style={{ background: 'var(--accent)' }}
      >
        {initial}
      </button>

      {open && (
        <div
          role="dialog"
          // Size to content so a full email fits (was a fixed 201px that clipped e.g.
          // a long work address such as first.lastname@example.com); clamp to a sane max, with the email's truncate + title
          // as the fallback for anything longer.
          className="absolute bottom-0 left-full z-50 ml-2 flex w-max min-w-[201px] max-w-[340px] flex-col gap-1 rounded-xl p-3"
          style={{
            background: 'var(--canvas-raised)',
            border: '1px solid var(--canvas-divider)',
            boxShadow: '0 6px 8px rgba(0, 0, 0, 0.04)',
          }}
        >
          {/* Email */}
          <div className="flex h-10 items-center truncate rounded-lg px-3 text-[13px] text-text-muted" title={email}>
            {email}
          </div>

          {/* Light / dark segmented control */}
          <ThemeSegmented />

          {/* Upgrade plan / Plan — billing feature; hidden entirely when plans are feature-flagged off
              (2026-08-17), since /upgrade does not exist then and this would be a dead link; also
              omitted when subscription is null
              (Stripe:Enabled off). "Plan" replaces "Upgrade plan" whenever the caller's tier is
              already 'pro' (M3, 2026-08-13 — dropped the `source==='stripe'` condition per the
              binding plan copy rule: there's nothing left to upgrade TO regardless of how that Pro
              tier was granted — manual override included). */}
          {featurePlans && subscription && (
            <Link
              to={routes.upgrade}
              onClick={() => setOpen(false)}
              className="flex h-10 items-center gap-2 rounded-lg px-2 text-[13px] text-text hover:bg-canvas-inset"
            >
              <CircleArrowUp className="h-5 w-5 text-text-muted" />
              <span>{subscription.tier === 'pro' ? 'Plan' : 'Upgrade plan'}</span>
              <span className="text-text-muted">• {planDisplayName(subscription.tier, plans.data)}</span>
            </Link>
          )}

          {/* Settings */}
          <Link
            to={routes.settings}
            onClick={() => setOpen(false)}
            className="flex h-10 items-center gap-2 rounded-lg px-2 text-[13px] text-text hover:bg-canvas-inset"
          >
            <SettingsIcon className="h-5 w-5 text-text-muted" />
            Settings
          </Link>

          {/* Log out */}
          <button
            type="button"
            onClick={onSignOut}
            className="flex h-10 items-center gap-2 rounded-lg px-2 text-[13px] text-text hover:bg-canvas-inset"
          >
            <LogOut className="h-5 w-5 text-text-muted" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

function ThemeSegmented() {
  const { theme, setTheme } = useTheme();
  const activeStyle = { background: 'var(--canvas-raised)', boxShadow: '0 0 16px rgba(0, 0, 0, 0.04)' };
  return (
    <div className="flex items-center gap-1 rounded-lg p-1" style={{ background: 'var(--canvas-inset)' }}>
      <button
        type="button"
        onClick={() => setTheme('light')}
        aria-label="Light mode"
        aria-pressed={theme === 'light'}
        className="flex h-6 flex-1 items-center justify-center rounded-md text-text-muted"
        style={theme === 'light' ? activeStyle : undefined}
      >
        <Sun className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setTheme('dark')}
        aria-label="Dark mode"
        aria-pressed={theme === 'dark'}
        className="flex h-6 flex-1 items-center justify-center rounded-md text-text-muted"
        style={theme === 'dark' ? activeStyle : undefined}
      >
        <Moon className="h-4 w-4" />
      </button>
    </div>
  );
}
